import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { fetchJson } from '../api.ts';

/**
 * Recompute engineer observations + scores for the org. Posts to the
 * jobs queue and returns a job_id immediately; the button polls
 * `useJobStatus` to surface progress + invalidates view caches once
 * the queue consumer marks the job succeeded. Wrapped this way
 * because derivation can exceed the ~15s Cloudflare gateway timeout
 * on the previous synchronous endpoint.
 *
 * Auth: WorkOS cookie + org-owner role at the route layer.
 */

export interface RecomputeStats {
  observations_inserted: number;
  scores_inserted: number;
  engineers: number;
  derivedContexts: number;
  duration_ms: number;
}

export interface EnqueueResponse {
  job_id: string;
  status: 'queued';
}

export interface JobStatus {
  id: string;
  kind: string;
  status: 'queued' | 'running' | 'succeeded' | 'failed';
  created_at: string;
  started_at: string | null;
  completed_at: string | null;
  error_kind: string | null;
  error_message: string | null;
  result_json: string | null;
}

export function useRecomputeObservations(orgSlug: string | null) {
  return useMutation({
    mutationFn: async (): Promise<EnqueueResponse> => {
      if (!orgSlug) throw new Error('useRecomputeObservations called without orgSlug');
      return fetchJson<EnqueueResponse>(`/v1/orgs/${orgSlug}/observations`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({}),
      });
    },
  });
}

/**
 * Poll a job's status. Repolls every 3s while queued/running and
 * stops once the row reaches a terminal state. Invalidates view
 * caches on success so matrix views pick up the recomputed ratings.
 */
export function useJobStatus(orgSlug: string | null, jobId: string | null) {
  const qc = useQueryClient();
  return useQuery<JobStatus, Error>({
    queryKey: ['job', orgSlug, jobId],
    enabled: orgSlug !== null && jobId !== null,
    queryFn: async () => {
      const job = await fetchJson<JobStatus>(`/v1/orgs/${orgSlug}/jobs/${jobId}`);
      if (job.status === 'succeeded') {
        void qc.invalidateQueries({ queryKey: ['view'] });
      }
      return job;
    },
    refetchInterval: (q) => {
      const s = q.state.data?.status;
      if (s === 'running' || s === 'queued') return 3_000;
      return false;
    },
    staleTime: 0,
  });
}
